/**
 * 공간별 소계 카드.
 *
 * 상담에서 고객이 제일 많이 하는 질문이 「욕실만 얼마예요?」 다. 견적은 공종
 * (누가 하나)으로 묶여 있어 욕실이 도배·타일·설비·바스에 흩어져 있는데, 이 표가
 * 그 축을 가로질러 한 줄로 답한다.
 *
 * **공간을 하나도 안 정한 견적에는 뜨지 않는다** — 빈 표는 화면만 길게 한다.
 */

import SectionCard from "@buildpy/ui/section-card";
import Money from "@/components/money";
import {
  hasAnySpace,
  SpaceCountable,
  spaceTotals,
} from "@buildpy/core/modules/quote/space-totals";
import { SnapshotData } from "@buildpy/core/modules/quote/types";

interface Props {
  data: SnapshotData;
  /** 회사의 공간 코드값. 표의 순서가 이 순서다. */
  spaces: { code: string; label: string }[];
}

/**
 * 스냅샷 항목에서 셀 것만 뽑는다. 발송본은 **그때의 표기**(`space_label`)를
 * 들고 있으므로, 나중에 회사가 라벨을 고쳐도 고객이 본 화면은 그대로다.
 */
const countableOf = (data: SnapshotData): SpaceCountable[] =>
  data.trades.flatMap((trade) => trade.items as SpaceCountable[]);

const SpaceSummary = ({ data, spaces }: Props) => {
  const items = countableOf(data);
  if (!hasAnySpace(items)) return null;

  // 발송본이 굳혀 둔 표기를 우선한다 — 없으면 지금 회사 목록으로 푼다.
  const frozen = new Map<string, string>();
  const walk = (rows: SnapshotData["trades"][number]["items"]): void => {
    for (const row of rows) {
      if (row.space_code && row.space_label) {
        frozen.set(row.space_code, row.space_label);
      }
      walk(row.children);
    }
  };
  for (const trade of data.trades) walk(trade.items);

  const list = spaces.map((s) => ({
    code: s.code,
    label: frozen.get(s.code) ?? s.label,
  }));
  const rows = spaceTotals(items, list);
  const total = rows.reduce((n, r) => n + r.amount, 0);

  return (
    <SectionCard padding="sm">
      <div className="mb-2 flex items-baseline justify-between gap-x-2">
        <h2 className="text-sm font-semibold">공간별</h2>
        <span className="text-xs text-muted-foreground">
          관리비·절사 전 금액
        </span>
      </div>

      <dl className="flex flex-col gap-y-1.5">
        {rows.map((row) => (
          <div
            key={row.code ?? "__none__"}
            className="flex items-baseline justify-between gap-x-3">
            <dt className="text-sm text-muted-foreground">
              {row.label}
              <span className="ml-1 text-xs opacity-70">{row.count}건</span>
            </dt>
            <dd className="text-sm font-medium">
              <Money value={row.amount} />
            </dd>
          </div>
        ))}

        <div className="mt-1 flex items-baseline justify-between gap-x-3 border-t border-border pt-1.5">
          <dt className="text-sm">합계</dt>
          <dd className="text-sm font-semibold">
            <Money value={total} />
          </dd>
        </div>
      </dl>
    </SectionCard>
  );
};

export default SpaceSummary;
